import React, { useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { ExternalLink, Terminal, Bot, Menu, X, UsersRound, Building } from 'lucide-react';
import Dashboard from './pages/Dashboard';
import DataWorkflow from './pages/DataWorkflow';
import ShieldxAI from './pages/AbhiAI';
import Team from './pages/Team';
import Login from './pages/Login';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';

function App() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const auth = useAuth(); // Move hook call to the top level of the component
  
  // Handle resize events - moved BEFORE conditional returns
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle logout
  const handleLogout = () => {
    auth.logout();
    window.location.href = '/login';
  };
  
  // Don't render the sidebar and header on the login page
  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-x-hidden">
      {/* Glowing Teal Accent */}
      <div className="fixed -top-20 -right-20 w-96 h-96 bg-teal-900/20 rounded-full blur-[100px] z-0"></div>
      <div className="fixed top-1/3 -left-20 w-80 h-80 bg-cyan-900/10 rounded-full blur-[100px] z-0"></div>
      
      {/* Grid Background */}
      <div className="fixed inset-0 z-0 opacity-10 animated-grid"></div>

      {/* Animated Security Scan Line */}
      <div className="fixed inset-0 z-0">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-teal-500 to-transparent absolute"
          style={{ 
            animation: 'scanVertical 8s infinite linear',
            boxShadow: '0 0 15px rgba(20, 184, 166, 0.5), 0 0 30px rgba(20, 184, 166, 0.3)'
          }}>
        </div>
        <div className="w-px h-full bg-gradient-to-b from-transparent via-teal-500 to-transparent absolute"
          style={{ 
            animation: 'scanHorizontal 10s infinite linear',
            boxShadow: '0 0 15px rgba(20, 184, 166, 0.5), 0 0 30px rgba(20, 184, 166, 0.3)'
          }}>
        </div>
      </div>

      {/* Navigation Sidebar - Desktop */}
      <div className="fixed left-0 top-0 bottom-0 w-20 bg-gradient-to-b from-slate-900 to-slate-800 border-r border-teal-900/20 backdrop-blur-sm z-50 flex flex-col items-center py-8 hidden md:flex">
        <Link to="/" className="mb-12 relative">
          <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-10 w-10" />
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full animate-ping"></div>
        </Link>
        
        <div className="flex flex-col items-center space-y-8 flex-grow">
          <Link 
            to="/"
            className={`p-3 rounded-lg transition-all ${location.pathname === '/' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-500 hover:text-teal-400'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="9" />
              <rect x="14" y="3" width="7" height="5" />
              <rect x="14" y="12" width="7" height="9" />
              <rect x="3" y="16" width="7" height="5" />
            </svg>
          </Link>
          <Link 
            to="/data-workflow"
            className={`p-3 rounded-lg transition-all ${location.pathname === '/data-workflow' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-500 hover:text-teal-400'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </Link>
          <Link 
            to="/abhi-ai"
            className={`p-3 rounded-lg transition-all ${location.pathname === '/abhi-ai' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-500 hover:text-teal-400'}`}>
            <Bot className="h-6 w-6" />
          </Link>
          <Link 
            to="/team"
            className={`p-3 rounded-lg transition-all ${location.pathname === '/team' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-500 hover:text-teal-400'}`}>
            <UsersRound className="h-6 w-6" />
          </Link>
        </div>
        
        <Link to="/abhi-ai" className="mt-auto bg-slate-800/50 p-3 rounded-lg border border-teal-900/30 hover:bg-teal-900/20 transition-all">
          <Terminal className="h-6 w-6 text-teal-500" />
        </Link>
      </div>

      {/* Main Content */}
      <div className="md:ml-20 relative z-10">
        {/* Navigation Header */}
        <header className="border-b border-teal-900/30 backdrop-blur-sm bg-slate-900/60 sticky top-0 z-40">
          <div className="container mx-auto px-2 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-8 w-8" />
                <span className="text-xl md:text-2xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-cyan-400">
                  Security<span className="text-teal-500"> Command Center</span>
                </span>
                <div className="h-1.5 w-1.5 rounded-full bg-teal-500 animate-pulse"></div>
              </div>
              
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md bg-teal-900/20 border border-teal-900/30 text-white md:hidden"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
              
              {/* Desktop header actions */}
              <div className="hidden md:flex items-center space-x-6">
                <div className="bg-slate-800 px-3 py-1.5 rounded-md border border-slate-700 text-sm text-slate-400 flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                  <span>SECURED CONNECTION</span>
                </div>
                <button
                  onClick={handleLogout} // Use the handler function instead of inline logic
                  className="bg-teal-900/20 hover:bg-teal-900/40 text-teal-400 px-3 py-1.5 rounded border border-teal-900/30 text-sm transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 bg-slate-900/95 z-30 md:hidden flex flex-col">
            <div className="flex flex-col items-center justify-center h-full space-y-8">
              <Link 
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`p-4 rounded-lg flex items-center space-x-3 transition-all w-4/5 ${location.pathname === '/' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-300 border border-slate-700'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="9" />
                  <rect x="14" y="3" width="7" height="5" />
                  <rect x="14" y="12" width="7" height="9" />
                  <rect x="3" y="16" width="7" height="5" />
                </svg>
                <span>Dashboard</span>
              </Link>
              <Link 
                to="/data-workflow"
                onClick={() => setMobileMenuOpen(false)}
                className={`p-4 rounded-lg flex items-center space-x-3 transition-all w-4/5 ${location.pathname === '/data-workflow' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-300 border border-slate-700'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
                <span>Data Workflow</span>
              </Link>
              <Link 
                to="/abhi-ai"
                onClick={() => setMobileMenuOpen(false)}
                className={`p-4 rounded-lg flex items-center space-x-3 transition-all w-4/5 ${location.pathname === '/abhi-ai' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-300 border border-slate-700'}`}>
                <Bot className="h-6 w-6" />
                <span>Abhi AI</span>
              </Link>
              <Link 
                to="/team"
                onClick={() => setMobileMenuOpen(false)}
                className={`p-4 rounded-lg flex items-center space-x-3 transition-all w-4/5 ${location.pathname === '/team' ? 'bg-teal-900/30 text-teal-400' : 'text-gray-300 border border-slate-700'}`}>
                <UsersRound className="h-6 w-6" />
                <span>Team</span>
              </Link>
              
              <button
                onClick={() => {
                  auth.logout();
                  window.location.href = '/login';
                  setMobileMenuOpen(false);
                }}
                className="mt-6 bg-teal-900/20 hover:bg-teal-900/40 text-teal-400 px-6 py-3 rounded-lg border border-teal-900/30 text-base transition-colors w-4/5 flex items-center justify-center"
              >
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}

        <Routes>
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/data-workflow" element={
            <ProtectedRoute>
              <DataWorkflow />
            </ProtectedRoute>
          } />
          <Route path="/abhi-ai" element={
            <ProtectedRoute>
              <ShieldxAI />
            </ProtectedRoute>
          } />
          <Route path="/team" element={
            <ProtectedRoute>
              <Team />
            </ProtectedRoute>
          } />
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Footer */}
        <footer className="border-t border-teal-900/30 backdrop-blur-sm bg-slate-900/60 py-6 relative z-10">
          <div className="container mx-auto px-4 md:px-6 text-center text-slate-400 text-sm">
            <div className="flex items-center justify-center mb-2">
              <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-5 w-5 mr-2" />
              <span className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-cyan-400">
                Abhi <span className="text-teal-500">Security Command Center</span>
              </span>
            </div>
            <p>Powered By Abhi Pvt Ltd © 2025.</p>
          </div>
        </footer>
      </div>
    </div>
  );
}

function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWrapper;
